import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, Select, Space } from "antd";
import { useParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { usePdfContext } from "@containers/PdfReport/context";
import { streamFacilityAiChat } from "@/containers/facility/AiAssistant/api";
import { generateReportDraft, reportDraftIsStale, reportDraftScope } from "./reportDraft.mjs";

export default function ReportAiSummary({ stream = streamFacilityAiChat }) {
  const { companyId, facilityId } = useParams();
  const aiAllowed = useAuth("facility_ai_chat");
  const facilityAllowed = useAuth("facility_view");
  const allowed = aiAllowed && facilityAllowed;
  const { form, reportGeneration, claimReportGeneration, setReportDraftBusy } = usePdfContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const request = useRef(null);
  const generate = useCallback(async () => {
    if (!allowed || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setReportDraftBusy(true);
    setError("");
    setProgress("Gathering report evidence…");
    try {
      const values = form.getFieldsValue(true);
      const scope = reportDraftScope(values);
      const result = await generateReportDraft({
        stream, values, companyId, facilityId,
        signal: controller.signal, onProgress: setProgress,
      });
      if (controller.signal.aborted) return;
      if (scope !== reportDraftScope(form.getFieldsValue(true))) {
        throw new Error("The report period or audience changed. Generate the summary again for the new settings.");
      }
      form.setFieldsValue(result);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.message || "The summary could not be generated.");
    } finally {
      if (request.current === controller) {
        request.current = null;
        setBusy(false);
        setReportDraftBusy(false);
      }
    }
  }, [allowed, companyId, facilityId, form, setReportDraftBusy, stream]);

  useEffect(() => {
    if (allowed && reportGeneration && claimReportGeneration(reportGeneration)) generate();
  }, [allowed, reportGeneration, claimReportGeneration, generate]);
  useEffect(() => () => {
    request.current?.abort();
    request.current = null;
    setReportDraftBusy(false);
  }, [companyId, facilityId, setReportDraftBusy]);

  return <section aria-label="Management summary" style={{ marginBottom: 24 }}>
    <Form.Item name="reportAudience" label="Report audience" initialValue="Facility leadership">
      <Select disabled={busy} options={["Facility leadership", "Finance", "Executives"].map(value => ({ label: value, value }))} />
    </Form.Item>
    <Space style={{ marginBottom: 12 }}>
      {allowed && <Button onClick={generate} loading={busy}>{form.getFieldValue("managementSummary") ? "Regenerate management summary" : "Generate management summary"}</Button>}
      {busy && <span role="status">{progress}</span>}
    </Space>
    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
    <Form.Item noStyle shouldUpdate>{() => reportDraftIsStale(form.getFieldsValue(true)) &&
      <Alert type="warning" message="The report period or audience changed. Regenerate the summary before previewing." style={{ marginBottom: 12 }} />}</Form.Item>
    <Form.Item name="managementSummary" label="Management summary" extra="Editable text included in the report preview and saved PDF.">
      <Input.TextArea disabled={busy} autoSize={{ minRows: 6, maxRows: 24 }} placeholder="Generate a sourced summary or write your report narrative here." />
    </Form.Item>
    <Form.Item name="managementSummarySources" label="Summary sources">
      <Input.TextArea readOnly autoSize={{ minRows: 2, maxRows: 8 }} />
    </Form.Item>
    <Form.Item name="aiReportScope" hidden><Input /></Form.Item>
  </section>;
}
